import React from "react";
import clsx from "clsx";
import { IntlShape, useIntl } from "react-intl";

import { PlatformDTO, SyncRunResultDTO } from "@app/api/generated";
import { LabelComponent } from "@app/core";
import { Tooltip } from "@app/core/tooltip/tooltip.component";

import { formatDuration } from "@app/util/format-duration";
import { getPlatformName } from "@app/util/platform-name";
import { RUN_COUNTERS } from "@app/modules/product-page-sync/run-counters";
import { useProcessedDate } from "@app/util/use-processed-date";

import styles from "@app/styles/dashboard/run-detail-sidebar.module.scss";
import { RUN_STATUS_LABEL_KEY } from "@app/util/run-status";
import { usePlatform } from "../use-platform";

// Backend default when a platform has no explicit grace period; mirrors platformSettings.gracePeriod.
const DEFAULT_GRACE_PERIOD_DAYS = 14;

/**
 * Per-counter explanation for the sidebar. The two grace counters ("Gaat offline" / "Blijft online")
 * weave in the platform's grace period + min-deal threshold so it's clear from how many deals and
 * after how many days pages go offline; they fall back to a number-less variant until the platform
 * config has loaded.
 */
const getCounterTooltip = (intl: IntlShape, counterKey: string, platform: PlatformDTO | null): string | undefined => {
  const graceDays = platform?.productPageSyncGracePeriodDays ?? DEFAULT_GRACE_PERIOD_DAYS;
  const minStayOnline = platform?.productPageSyncMinDealsToStayOnline ?? "undefined";
  const minBackOnline = platform?.productPageSyncMinDealsToBackOnline ?? "undefined";
  const minCreate = platform?.productPageSyncMinDealsToCreatePage ?? "undefined";
  const cleanUpAfterDays = platform?.productPageSyncCleanupAfterDays ?? "undefined";

  switch (counterKey) {
    case "pagesCreated":
      return intl.formatMessage({ id: "productPageSync.counter.created.tooltip" }, { minDeals: minCreate });
    case "pagesSetOnline":
      return intl.formatMessage({ id: "productPageSync.counter.online.tooltip" }, { minDeals: minBackOnline });
    case "pagesSetPendingOffline":
      return intl.formatMessage(
        { id: "productPageSync.counter.grace.tooltip" },
        { minDeals: minStayOnline, graceDays }
      );
    case "pagesResetGrace":
      return intl.formatMessage({ id: "productPageSync.counter.graceReset.tooltip" }, { minDeals: minStayOnline });
    case "pagesSetOffline":
      return intl.formatMessage({ id: "productPageSync.counter.offline.tooltip" }, { graceDays });
    case "pagesDeleted":
      return intl.formatMessage({ id: "productPageSync.counter.deleted.tooltip" }, { cleanUpAfterDays });
    case "errorCount":
      return intl.formatMessage({ id: "productPageSync.counter.errors.tooltip" });
    default:
      return undefined;
  }
};

interface IRunDetailSidebarProps {
  run: SyncRunResultDTO;
}

const RunDetailSidebar = ({ run }: IRunDetailSidebarProps): JSX.Element => {
  const intl = useIntl();
  const formatProcessedDate = useProcessedDate();
  const platform = usePlatform(run.platformId);

  return (
    <aside className={styles.sidebar}>
      <dl className={styles.meta}>
        <div>
          <dt>{intl.formatMessage({ id: "productPageSync.table.id" })}</dt>
          <dd>{run.runId}</dd>
        </div>
        <div>
          <dt>{intl.formatMessage({ id: "productPageSync.table.status" })}</dt>
          <dd>
            <LabelComponent
              type={run.status.toLowerCase()}
              label={intl.formatMessage({ id: RUN_STATUS_LABEL_KEY[run.status] })}
            />
          </dd>
        </div>
        <div>
          <dt>{intl.formatMessage({ id: "productPageSync.table.finishedAt" })}</dt>
          <dd>{run.finishedAt ? formatProcessedDate(new Date(run.finishedAt)) : "—"}</dd>
        </div>
        <div>
          <dt>{intl.formatMessage({ id: "productPageSync.table.duration" })}</dt>
          <dd>{formatDuration(run)}</dd>
        </div>
        <div>
          <dt>{intl.formatMessage({ id: "productPageSync.table.platform" })}</dt>
          <dd>{getPlatformName(intl, run.platformId)}</dd>
        </div>
      </dl>

      <dl className={clsx(styles.meta, styles.counters)}>
        {RUN_COUNTERS.map((counter) => {
          const tooltip = getCounterTooltip(intl, counter.key, platform);
          return (
            <div key={counter.key}>
              <dt className={styles.counterLabel}>
                {tooltip ? (
                  <Tooltip tooltip={tooltip}>{intl.formatMessage({ id: counter.labelId })}</Tooltip>
                ) : (
                  intl.formatMessage({ id: counter.labelId })
                )}
              </dt>
              <dd className={clsx(counter.color === "red" && run[counter.key] > 0 && styles.errorValue)}>
                {run[counter.key]}
              </dd>
            </div>
          );
        })}
      </dl>

      {run.errorMessage && (
        <div className={styles.errorBox}>
          <span className={styles.errorTitle}>{intl.formatMessage({ id: "productPageSync.detail.errorMessage" })}</span>
          <p>{run.errorMessage}</p>
        </div>
      )}
    </aside>
  );
};

export { RunDetailSidebar };
